import { addDays, differenceInDays } from 'date-fns';
import { injectable } from 'inversify';

import EpgService from '../services/EpgService';
import { EPG_TYPE } from '../constants';
import { getNamedModule } from '../modules/container';
import type { PlaylistItem } from '../../types/playlist';
import type { EpgChannel, EpgProgram } from '../../types/epg';
import { logWarn } from '../logger';
import { allSettledValues } from '../utils/common';

@injectable()
export default class EpgController {
  /**
   * Update the start and end time properties of the given programs with the current date.
   * This can be used when having a static schedule or while developing
   */
  private generateDemoPrograms(programs: EpgProgram[]) {
    const today = new Date();
    const startDate = new Date(programs[0]?.startTime);

    // this makes sure that the start of the day is correct. `startOfDay(startDate)` doesn't work since it can yield
    // a different date depending on the timezone.
    // for example, given a startTime of `2022-08-03T23:00:00Z` will parse to `2022-08-04T01:00:00+0200` in
    // Europe/Amsterdam (GMT+2) which makes startOfDay return `2022-08-04T00:00:00+0200`.
    const utcStartDate = new Date(Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth(), startDate.getUTCDate()));
    const daysDelta = differenceInDays(today, utcStartDate);

    return programs.map((program, idx) => ({
      ...program,
      id: `${program.id}_${idx}`,
      startTime: addDays(new Date(program.startTime), daysDelta).toJSON(),
      endTime: addDays(new Date(program.endTime), daysDelta).toJSON(),
    }));
  }

  /**
   * Ensure the given data validates to the EpgProgram schema
   */
  parsePrograms = (programs: EpgProgram[], channel: EpgChannel) => {
    return channel.demo ? this.generateDemoPrograms(programs) : programs;
  };

  /**
   * Get all schedules for the given PlaylistItem's
   */
  getChannels = async (items: PlaylistItem[], startTime?: number, endTime?: number): Promise<EpgChannel[]> => {
    const epgService = this.getEpgService(items[0]);

    if (!epgService) throw new Error('EPG service not configured properly');

    const channels = await epgService.fetchChannels(items, startTime, endTime);
    const programs = await epgService.fetchPrograms(items, startTime, endTime);

    return channels.map((channel) => {
      const channelPrograms = programs.filter((program) => program.channelId === channel.id) || channel.programs;

      return {
        ...channel,
        programs: this.parsePrograms(channelPrograms, channel),
      } as EpgChannel;
    });
  };

  getEpgService = (item: PlaylistItem) => {
    const scheduleType = item?.scheduleType?.toLocaleLowerCase() || EPG_TYPE.jwp;
    const service = getNamedModule(EpgService, scheduleType, false);

    if (!service) {
      logWarn('EpgController', `No epg service was added for the ${scheduleType} schedule type`);
    }

    return service;
  };

  /**
   * Get all schedules for the given PlaylistItem's
   */
  getSchedules = async (items: PlaylistItem[], startTime?: number, endTime?: number) => {
    // group all items per scheduleType (provider)
    const grouped = items.reduce<Record<string, PlaylistItem[]>>((acc, item) => {
      const scheduleType = item.scheduleType || EPG_TYPE.jwp;
      acc[scheduleType] ??= [];
      acc[scheduleType].push(item);
      return acc;
    }, {});

    // get all channels for each schedule type
    const results = await allSettledValues(Object.values(grouped).map((group) => this.getChannels(group, startTime, endTime)));

    // create a dictionary with the media id so we can sort the channels based on the original `items` order
    const referenceDictionary = Object.fromEntries(results.flat().map((channel) => [channel.id, channel]));

    return items.map((item) => referenceDictionary[item.mediaid]).filter((item) => !!item);
  };
}
